import * as React from 'react';
import { IconQuestionMark } from '@tabler/icons';
import { cn } from '#root/lib/utils';
import { Usd, usdText } from '../../primitives/usd/usd';
import styles from './price-total.module.scss';

export interface PriceTotalProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'children'> {
  /** Etiqueta del monto ("Total pagado") */
  label: React.ReactNode;
  /** Monto en dólares */
  amount: number | string | null | undefined;
  /**
   * Con `onClick` es un botón con un `?` que abre el detalle; sin él, un bloque estático.
   * Un botón necesita `aria-label` que diga qué abre; el componente le antepone el monto
   * ("Total pagado $1,390. Ver detalle del total pagado") para que el lector lo anuncie.
   */
  onClick?: () => void;
  /** Ocupa todo el ancho de su contenedor, con la etiqueta y el monto en los extremos */
  block?: boolean;
}

/**
 * PriceTotal Component - Design System
 *
 * Sub-card de un solo monto en dólares (`Usd`), con el mismo material que `PricePair`.
 * Para totales ya cerrados, como lo que pagó el comprador en un pedido ONE.
 *
 * @example
 * <PriceTotal label="Total pagado" amount={1595} />
 */
const PriceTotal = React.forwardRef<HTMLElement, PriceTotalProps>(
  ({ label, amount, onClick, block, className, ...props }, ref) => {
    const content = (
      <span className={styles.term}>
        <small>{label}</small>
        <strong>
          <Usd value={amount} />
        </strong>
      </span>
    );
    const classes = cn(
      styles.priceTotal,
      block && styles.block,
      onClick && styles.interactive,
      className
    );

    if (onClick) {
      // El aria-label del caller dice la acción; delante va el monto, que el label solo taparía
      const { 'aria-label': action, ...buttonProps } = props;
      const amountText =
        typeof label === 'string' && usdText(amount) ? `${label} ${usdText(amount)}` : '';
      const name = [amountText, action].filter(Boolean).join('. ') || undefined;
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          className={classes}
          aria-haspopup="dialog"
          aria-label={name}
          onClick={onClick}
          {...(buttonProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
          {content}
          <IconQuestionMark className={styles.help} size={14} aria-hidden="true" />
        </button>
      );
    }

    return (
      <div ref={ref as React.Ref<HTMLDivElement>} className={classes} {...props}>
        {content}
      </div>
    );
  }
);

PriceTotal.displayName = 'PriceTotal';

export { PriceTotal };
